'use client';

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { fetchCertificateByContract, trackCertificateVerificationView } from '@/lib/api';
import {
    coerceVerificationTemplateId,
    type VerificationTemplateId,
} from '@/lib/verificationTemplates';
import TemplateHeroGradient from './templates/TemplateHeroGradient';
import TemplateCardQr from './templates/TemplateCardQr';
import TemplateMinimal from './templates/TemplateMinimal';
import TemplateDiploma from './templates/TemplateDiploma';
import TemplateDiplomaWide from './templates/TemplateDiplomaWide';
import TemplateTrustPanel from './templates/TemplateTrustPanel';
import type { VerifiedCertificate, VerificationTenantInfo } from './templates/types';

type VerifyApiResponse = {
    name: string;
    competency: string;
    issued: string;
    expiry: string;
    status: string;
    contract: string;
    tokenId: number;
    networkId: number;
    imageURI?: string;
    explorerUrl?: string;
    tenant?: VerificationTenantInfo | null;
    shortUrl?: string | null;
};

function computeStatus(rawStatus: string | undefined, expiry: string): 'valid' | 'revoked' | 'expired' {
    const now = new Date();
    const expiryDate = new Date(expiry);
    if (rawStatus === 'revoked') return 'revoked';
    if (expiryDate < now) return 'expired';
    return 'valid';
}

function resolveTemplateId(
    override: string | null,
    fromTenant: string | null | undefined,
): VerificationTemplateId {
    // Backwards compatible: v=Standard
    if (override === 'Standard') return 'STANDARD_CARD';
    if (override === 'SWM') return 'SWM_VIOLET';
    if (override === 'Minimal') return 'MINIMAL_BADGE';

    const coercedOverride = coerceVerificationTemplateId(override);
    if (coercedOverride) return coercedOverride;

    const coercedTenant = coerceVerificationTemplateId(fromTenant);
    if (coercedTenant) return coercedTenant;

    // Preserve current behavior: default to the Hero (Gradient) style.
    return 'SWM_VIOLET';
}

export default function CertificateVerificationClient({
    contract,
    tenantSlug,
}: {
    contract: string;
    tenantSlug?: string;
}) {
    const searchParams = useSearchParams();
    const tokenIdRaw = searchParams.get('a');
    const networkIdRaw = searchParams.get('n') || (process.env.NEXT_PUBLIC_DEFAULT_NETWORK_ID ?? '24772477');
    const templateOverride = searchParams.get('tpl') ?? searchParams.get('v');

    const [certificate, setCertificate] = useState<VerifiedCertificate | null>(null);
    const [tenant, setTenant] = useState<VerificationTenantInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!contract || !tokenIdRaw || !networkIdRaw) return;

            setLoading(true);
            setError(null);

            try {
                const cert = (await fetchCertificateByContract(
                    contract,
                    parseInt(tokenIdRaw, 10),
                    parseInt(networkIdRaw, 10),
                    tenantSlug,
                )) as VerifyApiResponse;

                const status = computeStatus(cert.status, cert.expiry);
                trackCertificateVerificationView({
                    contract,
                    tokenId: parseInt(tokenIdRaw, 10),
                    networkId: parseInt(networkIdRaw, 10),
                    tenantSlug,
                });

                setCertificate({
                    name: cert.name,
                    competency: cert.competency,
                    issued: cert.issued,
                    expiry: cert.expiry,
                    status,
                    contract: cert.contract,
                    tokenId: cert.tokenId,
                    networkId: cert.networkId,
                    imageURI: cert.imageURI,
                    explorerUrl: cert.explorerUrl,
                    shortUrl: cert.shortUrl,
                });

                setTenant(cert.tenant ?? null);
            } catch (err) {
                console.error('Error fetching certificate', err);
                setError('Certificate not found');
                setCertificate(null);
                setTenant(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [contract, tokenIdRaw, networkIdRaw, tenantSlug]);

    const verificationCode = useMemo(() => {
        if (!certificate) return '';
        return `${certificate.contract}/${certificate.tokenId}/${certificate.networkId}`;
    }, [certificate]);

    const verificationUrl = useMemo(() => {
        const baseUrl = (process.env.NEXT_PUBLIC_VERIFICATION_SITE_URL || '').replace(/\/+$/, '');
        const path = tenantSlug ? `/${tenantSlug}/${contract}` : `/${contract}`;
        return `${baseUrl}${path}?a=${tokenIdRaw ?? ''}&n=${networkIdRaw ?? ''}`;
    }, [contract, tenantSlug, tokenIdRaw, networkIdRaw]);

    const templateId = useMemo(() => {
        return resolveTemplateId(templateOverride, tenant?.verificationTemplateId);
    }, [templateOverride, tenant?.verificationTemplateId]);

    if (loading || !certificate) {
        return (
            <div className="min-h-screen w-full flex items-center justify-center bg-background text-foreground p-10">
                {error ? error : 'Loading certificate…'}
            </div>
        );
    }

    const props = {
        certificate,
        tenant,
        verificationCode,
        verificationUrl,
        // Prefer the short link so the page's QR encodes the same string as the
        // one on the issued certificate.
        qrUrl: certificate.shortUrl || verificationUrl,
    };

    switch (templateId) {
        case 'STANDARD_CARD':
            return <TemplateCardQr {...props} />;
        case 'MINIMAL_BADGE':
            return <TemplateMinimal {...props} />;
        case 'DIPLOMA_ELEGANT':
            return <TemplateDiploma {...props} />;
        case 'DIPLOMA_WIDE':
            return <TemplateDiplomaWide {...props} />;
        case 'TRUST_PANEL':
            return <TemplateTrustPanel {...props} />;
        case 'SWM_VIOLET':
        default:
            return <TemplateHeroGradient {...props} />;
    }
}
